import { Role } from '../services/userService';

/** Chip/badge colors assigned by hierarchy rank (most senior role first), not by name —
 * any role fetched from `app.user_role` gets one automatically, cycling if there are more
 * roles than colors. Deliberately excludes green/red/orange (reserved for active/danger/brand
 * accent elsewhere in this page) so a role color is never mistaken for a status. */
const ROLE_COLOR_CLASSES = ['role-color-0', 'role-color-1', 'role-color-2', 'role-color-3', 'role-color-4'];

export const roleColorClass = (roleName: string, roles: Role[]): string => {
  const sorted = [...roles].sort((a, b) => (a.role_hierarchy ?? Number.MAX_SAFE_INTEGER) - (b.role_hierarchy ?? Number.MAX_SAFE_INTEGER));
  const idx = sorted.findIndex((r) => r.name === roleName);
  return ROLE_COLOR_CLASSES[(idx < 0 ? 0 : idx) % ROLE_COLOR_CLASSES.length];
};

/** Human-readable label for a role — its `display_name` from the DB, falling back to the raw
 * name (title-cased) when the roles catalog hasn't loaded yet or the role isn't in it. */
export const formatRoleLabel = (role: Role | null | undefined, fallbackName: string): string =>
  role?.display_name || fallbackName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Top 2 hierarchy tiers — mirrors the Hasura roles currently granted insert/update/delete on
 * app.user_assignment and the `role` column on app.user (administrator=1, executive=2 in the
 * current seed). A new role meant to carry that same access must be seeded at hierarchy 1 or 2
 * AND granted the matching Hasura permissions server-side — this threshold alone doesn't grant it. */
export const ADMIN_TIER_MAX_HIERARCHY = 2;

/** Any internal/staff role — every tier except the plain "customer" role (hierarchy 5 in the
 * current seed). Used for gates that should admit all staff, e.g. Customer Health record edits
 * or bypassing shared-opportunity-link restrictions. */
export const STAFF_TIER_MAX_HIERARCHY = 4;

export const isAdminTier = (hierarchy?: number | null): boolean =>
  hierarchy != null && hierarchy <= ADMIN_TIER_MAX_HIERARCHY;

export const isStaffTier = (hierarchy?: number | null): boolean =>
  hierarchy != null && hierarchy <= STAFF_TIER_MAX_HIERARCHY;
